import {
    Box,
    Card,
    CardContent,
    Chip,
    Typography,
} from '@mui/material';
import React from 'react';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import PrinterPrintProgressBar, {
    resolvePrinterPrintProgressPercent,
} from './PrinterPrintProgressBar';
import PrinterSnapshotThumb from './PrinterSnapshotThumb';
import PrinterFilamentSlots from './PrinterFilamentSlots';

export default function ActivePrinterCard({ card, progressMap, onClick, onFilamentsChanged }) {
    const brandBadge = getPrinterBrandBadgeProps(card.brand);
    const progress = resolvePrinterPrintProgressPercent(progressMap, card.printer_uuid);

    const handleKeyDown = (event) => {
        // Portal-rendered dialog inputs bubble key events through React to this
        // card; only activate navigation when the card itself is focused.
        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.(event);
        }
    };

    const handleCardClick = (event) => {
        if (event.target.closest('[data-printer-filament-slots]')) {
            return;
        }
        onClick?.(event);
    };

    return (
        <Card
            variant="outlined"
            role="button"
            tabIndex={0}
            onClick={handleCardClick}
            onKeyDown={handleKeyDown}
            sx={{
                height: '100%',
                borderColor: 'success.main',
                boxShadow: '0 0 0 1px rgba(46, 125, 50, 0.35)',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                    boxShadow: 4,
                },
                '&:focus-visible': {
                    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                },
            }}
        >
            <PrinterSnapshotThumb
                printer={card}
                alt={`Câmera de ${card.printer_name || 'impressora'}`}
                enablePreview={false}
                enableTooltip={false}
            />
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                        {brandBadge ? (
                            <Chip label={card.printer_name} size="small" {...brandBadge} />
                        ) : (
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                                {card.printer_name}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        mb: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    title={card.file_name}
                >
                    {card.file_name}
                </Typography>
                <PrinterPrintProgressBar
                    percent={progress}
                    color="success"
                    sx={{ mb: 1 }}
                />
                <PrinterFilamentSlots
                    printerUuid={card.printer_uuid}
                    printerName={card.printer_name}
                    filaments={card.filaments}
                    onChanged={(next) => onFilamentsChanged?.(card.printer_uuid, next)}
                />
            </CardContent>
        </Card>
    );
}
